import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Wrench, CheckCircle, XCircle, Loader2, Star, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RATING_TAGS = ['Buen trabajo', 'Puntual', 'Atento', 'Precio justo', 'Respetuoso'];

export default function ManageTicket() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showThanksScreen, setShowThanksScreen] = useState(false);
  const autoActionDone = useRef(false);

  // Rating state
  const [revealedProviders, setRevealedProviders] = useState([]);
  const [ratings, setRatings] = useState({}); // { providerId: { stars, tags } }
  const [submittedRatings, setSubmittedRatings] = useState({}); // { providerId: true }
  const [ratingLoading, setRatingLoading] = useState({});

  useEffect(() => {
    if (token) {
      fetchTicket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-execute email action after ticket loads
  useEffect(() => {
    const action = searchParams.get('action');
    if (!action || !ticket || autoActionDone.current) return;
    if (ticket.status !== 'Publicado') return; // ticket ya está en estado final, no hacer nada

    autoActionDone.current = true;

    if (action === 'resolve-outside') {
      executeAutoAction('Resuelto por fuera de Providers', 'resolve-outside');
    } else if (action === 'cancel') {
      executeAutoAction('Cancelado', 'cancel');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket, searchParams]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/manage/${token}`);
      const t = response.data;
      setTicket(t);
      if (t.status === 'Concretado') {
        fetchRevealedProviders();
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('No se pudo cargar la solicitud. Verifica el enlace.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevealedProviders = async () => {
    try {
      const { data } = await axios.get(`${API}/manage/${token}/revealed-providers`);
      setRevealedProviders(data);
    } catch {
      // Silent — no providers revealed is valid
    }
  };

  const executeAutoAction = async (newStatus, actionType) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/manage/${token}/status`, null, {
        params: { new_status: newStatus }
      });
      if (actionType === 'resolve-outside') {
        setShowThanksScreen(true);
      } else {
        toast.success('Estado actualizado correctamente');
        fetchTicket();
      }
    } catch (error) {
      // If the ticket is already in that state or already final, just load normally
      const detail = error.response?.data?.detail || '';
      if (!detail.includes('Invalid transition')) {
        toast.error(detail || 'Error al actualizar el estado');
      }
      fetchTicket();
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/manage/${token}/status`, null, {
        params: { new_status: newStatus }
      });
      toast.success('Estado actualizado correctamente');
      fetchTicket();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    } finally {
      setUpdating(false);
    }
  };

  const setRatingStars = (providerId, stars) => {
    setRatings(prev => ({
      ...prev,
      [providerId]: { ...prev[providerId], stars, tags: prev[providerId]?.tags || [] }
    }));
  };

  const toggleRatingTag = (providerId, tag) => {
    setRatings(prev => {
      const current = prev[providerId] || { stars: 0, tags: [] };
      const tags = current.tags.includes(tag)
        ? current.tags.filter(t => t !== tag)
        : [...current.tags, tag];
      return { ...prev, [providerId]: { ...current, tags } };
    });
  };

  const submitRating = async (providerId) => {
    const r = ratings[providerId];
    if (!r?.stars) { toast.error('Seleccioná al menos una estrella'); return; }
    setRatingLoading(prev => ({ ...prev, [providerId]: true }));
    try {
      await axios.post(`${API}/manage/${token}/rate`, {
        provider_id: providerId,
        stars: r.stars,
        tags: r.tags
      });
      setSubmittedRatings(prev => ({ ...prev, [providerId]: true }));
      toast.success('¡Calificación guardada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la calificación');
    } finally {
      setRatingLoading(prev => ({ ...prev, [providerId]: false }));
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Publicado': 'bg-blue-100 text-blue-700',
      'Concretado': 'bg-green-100 text-green-700',
      'Expirado': 'bg-red-100 text-red-700',
      'Cancelado': 'bg-gray-100 text-gray-700',
      'Resuelto por fuera de Providers': 'bg-purple-100 text-purple-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'Publicado': 'Publicado',
      'Concretado': 'Concretado',
      'Expirado': 'Expirado',
      'Cancelado': 'Cancelado',
      'Resuelto por fuera de Providers': 'Resuelto por fuera de Providers',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary-strong)' }} />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>Solicitud no encontrada</h2>
        <Button onClick={() => navigate('/')} style={{ backgroundColor: 'var(--primary-strong)' }}>
          Volver al inicio
        </Button>
      </div>
    );
  }

  if (showThanksScreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4" data-testid="thanks-screen">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">¡Muchas gracias por avisarnos!</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            Nos ayuda muchísimo saber cómo terminó tu solicitud.
            Esperamos poder ayudarte nuevamente en una próxima oportunidad.
          </p>
          <Button
            data-testid="thanks-go-home-btn"
            onClick={() => navigate('/')}
            style={{ backgroundColor: 'var(--primary-strong)' }}
          >
            Ir al inicio
          </Button>
        </div>
      </div>
    );
  }

  const canUpdateStatus = ticket.status === 'Publicado';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="manage-ticket-page">
      <header className="bg-white px-4 py-5 border-b border-gray-200">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>Gestionar Solicitud</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusBadge(ticket.status)}`}>
              {getStatusLabel(ticket.status)}
            </span>
            <span className="text-sm text-gray-500">ID: {ticket.id.slice(0, 8)}</span>
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--dark-primary)' }}>{ticket.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span 
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--primary-strong)' }}
                >
                  <Wrench className="w-4 h-4" />
                  {ticket.category}
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  <MapPin className="w-4 h-4" />
                  {ticket.zone}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Descripción</h3>
              <p className="text-gray-700 leading-relaxed">{ticket.description}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500 mb-1">Fecha de publicación</p>
                <p className="font-medium">{formatDate(ticket.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Expira el</p>
                <p className="font-medium">{formatDate(ticket.expires_at)}</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500 mb-1">Contactos revelados</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>{ticket.reveal_count}</p>
              </div>
            </div>
          </div>

          {canUpdateStatus && (
            <div className="pt-6 border-t space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--dark-primary)' }}>
                  Actualizar estado
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Marcá tu solicitud como cancelada, concretada o resuelta por otro medio
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Button
                  data-testid="status-btn-Cancelado"
                  onClick={() => handleStatusUpdate('Cancelado')}
                  disabled={updating}
                  variant="outline"
                  className="justify-start"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelar solicitud
                </Button>

                <Button
                  data-testid="status-btn-Concretado"
                  onClick={() => handleStatusUpdate('Concretado')}
                  disabled={updating}
                  variant="outline"
                  className="justify-start"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Trabajo concretado
                </Button>

                <Button
                  data-testid="status-btn-ResueltaFuera"
                  onClick={() => executeAutoAction('Resuelto por fuera de Providers', 'resolve-outside')}
                  disabled={updating}
                  variant="outline"
                  className="justify-start sm:col-span-2"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Lo resolví fuera de Providers
                </Button>
              </div>
            </div>
          )}

          {/* Rating section — only visible when CONCRETADO */}
          {ticket.status === 'Concretado' && (
            <div className="pt-6 border-t" data-testid="rating-section">
              <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--dark-primary)' }}>
                Calificar proveedor
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Calificá a los proveedores que contactaste
              </p>

              {revealedProviders.length === 0 ? (
                <p className="text-sm text-gray-400 italic" data-testid="no-providers-msg">
                  Ningún proveedor reveló tus datos de contacto aún.
                </p>
              ) : (
                <div className="space-y-5">
                  {revealedProviders.map((p) => (
                    <div
                      key={p.id}
                      className="bg-gray-50 rounded-xl p-4 space-y-4"
                      data-testid={`provider-rating-card-${p.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ backgroundColor: p.avatar_color }}
                        >
                          {p.username.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-800">{p.username}</span>
                      </div>

                      {submittedRatings[p.id] ? (
                        <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          ¡Calificación enviada! Gracias.
                        </p>
                      ) : (
                        <>
                          {/* Stars */}
                          <div>
                            <p className="text-xs text-gray-500 mb-2">¿Cómo fue el trabajo?</p>
                            <div className="flex gap-1" data-testid={`stars-${p.id}`}>
                              {[1, 2, 3, 4, 5].map((s) => (
                                <button
                                  key={s}
                                  type="button"
                                  onClick={() => setRatingStars(p.id, s)}
                                  className="transition-transform hover:scale-110"
                                >
                                  <Star
                                    className="w-8 h-8"
                                    fill={(ratings[p.id]?.stars || 0) >= s ? '#F59E0B' : 'none'}
                                    stroke={(ratings[p.id]?.stars || 0) >= s ? '#F59E0B' : '#D1D5DB'}
                                  />
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Tags */}
                          <div>
                            <p className="text-xs text-gray-500 mb-2">Opciones adicionales (opcional)</p>
                            <div className="flex flex-wrap gap-2">
                              {RATING_TAGS.map((tag) => {
                                const selected = ratings[p.id]?.tags?.includes(tag);
                                return (
                                  <button
                                    key={tag}
                                    type="button"
                                    onClick={() => toggleRatingTag(p.id, tag)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                                      selected
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                    data-testid={`tag-${p.id}-${tag}`}
                                  >
                                    {tag}
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <Button
                            data-testid={`submit-rating-${p.id}`}
                            onClick={() => submitRating(p.id)}
                            disabled={ratingLoading[p.id] || !ratings[p.id]?.stars}
                            size="sm"
                            style={{ backgroundColor: 'var(--primary-strong)' }}
                          >
                            {ratingLoading[p.id]
                              ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" />Guardando...</>
                              : 'Enviar calificación'}
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
